import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Cell, HeaderCell, HeaderRow, Row, SortLabel, Table, TableBody, TableHead } from 'components';
import { IPaymentSetCalculations, IReceivedTipsData } from 'types';
import { centsToDollarsDisplay, formatReceivedTipsData, toTitleCase } from 'utils';
import { CSVFormatter } from 'utils/csvformatter';
import { IExportData } from './types';

const TableWrapper = styled.section`
  width: 100%;
`;

interface IReceivedTipsProps {
  header: (data?: IExportData) => React.ReactNode;
  calculations?: IPaymentSetCalculations;
  isLoading?: boolean;
}

const headers: { label: string; value: keyof IReceivedTipsData }[] = [
  { label: 'Payee', value: 'payee' },
  { label: 'Day Segment', value: 'daySegment' },
  { label: 'Job', value: 'jobTitle' },
  { label: 'Duration (Mins)', value: 'duration' },
  { label: 'Received From', value: 'receivedFrom' },
  { label: 'Received Tips', value: 'receivedTips' },
  { label: 'Received Autograts', value: 'receivedAutograts' },
];

const ReceivedTips = ({ header, calculations, isLoading }: IReceivedTipsProps) => {
  const [sortBy, setSortBy] = useState<{ field: keyof IReceivedTipsData; direction: 'asc' | 'desc' }>({ field: 'daySegment', direction: 'asc' });

  const data = formatReceivedTipsData(calculations, sortBy);
  const exportReceivedTipsCSV = async () => CSVFormatter.receivedTipsPayout(data);

  return (
    <>
      {header({ exportFunc: exportReceivedTipsCSV, fileName: 'ReceivedTips' })}
      <TableWrapper data-cy="table">
        <Table scrollable sticky data-cy="received-tips-table">
          <TableHead>
            <HeaderRow sticky>
              {headers.map(({ label, value }) => (
                <HeaderCell key={`${value}-header`}>
                  <SortLabel
                    label={label}
                    data-cy={`${value}-label`}
                    isSorted={sortBy.field === value}
                    isSortedDesc={sortBy.direction === 'asc'}
                    isLoading={isLoading}
                    onClick={() => setSortBy({ field: value, direction: sortBy.direction === 'asc' ? 'desc' : 'asc' })}
                  />
                </HeaderCell>
              ))}
            </HeaderRow>
          </TableHead>
          <TableBody>
            {data.map((data: IReceivedTipsData, idx) => (
              <Row key={`received-${idx}`}>
                <Cell wrapText minWidth={150} data-label="Payee" fontWeight="semiBold">
                  {isLoading ? <Skeleton width={50} /> : data.payee || '--'}
                </Cell>
                <Cell wrapText data-label="DaySegment">
                  {isLoading ? <Skeleton width={50} /> : data.daySegment || '--'}
                </Cell>
                <Cell data-label="JobTitle">{isLoading ? <Skeleton width={50} /> : toTitleCase(data.jobTitle || '--')}</Cell>
                <Cell data-label="Duration">{isLoading ? <Skeleton width={50} /> : data.duration || '--'}</Cell>
                <Cell data-label="ReceivedFrom">{isLoading ? <Skeleton width={50} /> : toTitleCase(data.receivedFrom || '--')}</Cell>
                <Cell data-label="ReceivedTips" fontWeight="semiBold">
                  {isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(data.receivedTips)}
                </Cell>
                <Cell data-label="ReceivedAutograts" fontWeight="semiBold">
                  {isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(data.receivedAutograts)}
                </Cell>
              </Row>
            ))}
          </TableBody>
        </Table>
      </TableWrapper>
    </>
  );
};

export default ReceivedTips;
